/**
 * Base Templates Management Page
 * Card grid layout for managing base template configurations
 */

import {
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  AlertCircle,
  ChevronRight,
  Database,
  Download,
  Edit,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  X,
  XCircle
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import {
  CreateTeableSeedConfigInput,
  TeableSeedConfig,
  UpdateTeableSeedConfigInput
} from '../../types/teableSeedConfig.types';
import { supabase } from '../../utils/supabaseClient';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { TableFieldsEditor } from './TableFieldsEditor';

type FilterStatus = 'all' | 'active' | 'inactive';
type FilterType = 'all' | 'system' | 'custom';

type TableEntry = {
  tablename: string;
  table_json: Record<string, unknown>;
  sample_json: Record<string, unknown>;
  tableJsonString: string;
  sampleJsonString: string;
};

type BulkImportData = {
  baseName?: string;
  description?: string;
  image?: string;
  status?: 'active' | 'inactive';
  system_custom?: 'system' | 'custom';
  tables: Array<{
    tablename: string;
    table_json: Record<string, unknown>;
    sample_json: Record<string, unknown>;
  }>;
};

type BulkImportPreview = {
  baseName?: string;
  description?: string;
  image?: string;
  status: 'active' | 'inactive';
  system_custom: 'system' | 'custom';
  tables: Array<{
    tablename: string;
    table_json: Record<string, unknown>;
    sample_json: Record<string, unknown>;
    validation: {
      isValid: boolean;
      errors: string[];
      warnings: string[];
    };
  }>;
  totalTables: number;
  validTables: number;
  invalidTables: number;
};

const TeableConfigPage: React.FC = () => {
  const { toast } = useToast();

  // State
  const [configs, setConfigs] = useState<TeableSeedConfig[]>([]);
  const [filteredConfigs, setFilteredConfigs] = useState<TeableSeedConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedBaseName, setSelectedBaseName] = useState<string | null>(null);

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteBaseDialogOpen, setIsDeleteBaseDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<TeableSeedConfig | null>(null);
  const [selectedBaseForDelete, setSelectedBaseForDelete] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<TeableSeedConfig | null>(null);
  const [editingBase, setEditingBase] = useState<string | null>(null);
  const [baseEditFormData, setBaseEditFormData] = useState<{
    description: string;
    image: string;
    status: 'active' | 'inactive';
    system_custom: 'system' | 'custom';
  }>({
    description: '',
    image: '',
    status: 'active',
    system_custom: 'custom',
  });

  // Bulk import states
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [bulkImportPreview, setBulkImportPreview] = useState<BulkImportPreview | null>(null);
  const [isValidatingBulkImport, setIsValidatingBulkImport] = useState(false);
  const [bulkImportErrors, setBulkImportErrors] = useState<string[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateTeableSeedConfigInput>({
    name: '',
    description: '',
    image: '',
    tablename: '',
    table_json: {},
    sample_json: {},
    status: 'active',
    system_custom: 'custom',
  });

  const [tableJsonString, setTableJsonString] = useState('{}');
  const [sampleJsonString, setSampleJsonString] = useState('{}');
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [showVisualEditor, setShowVisualEditor] = useState(true);
  const [jsonValidationResult, setJsonValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);

  // Multiple table entries state
  const [tableEntries, setTableEntries] = useState<TableEntry[]>([
    {
      tablename: '',
      table_json: {},
      sample_json: {},
      tableJsonString: '{}',
      sampleJsonString: '{}',
    },
  ]);

  // File upload refs
  const tableJsonFileRef = useRef<HTMLInputElement>(null);
  const sampleJsonFileRef = useRef<HTMLInputElement>(null);
  const bulkImportFileRef = useRef<HTMLInputElement>(null);

  // Sample JSON templates for Teable API
  const getSampleTableJson = () => {
    return {
      name: "Contacts",
      dbTableName: "contacts",
      description: "Customer contact information",
      icon: "👤",
      fields: [
        {
          type: "singleLineText",
          name: "Name",
          dbFieldName: "name",
          description: "Contact full name"
        },
        {
          type: "singleLineText",
          name: "Email",
          dbFieldName: "email",
          description: "Contact email address"
        },
        {
          type: "singleLineText",
          name: "Phone",
          dbFieldName: "phone",
          description: "Contact phone number"
        },
        {
          type: "singleSelect",
          name: "Status",
          dbFieldName: "status",
          description: "Contact status",
          options: {
            choices: [
              { name: "Active", color: "green" },
              { name: "Inactive", color: "red" },
              { name: "Pending", color: "yellow" }
            ]
          }
        },
        {
          type: "number",
          name: "Age",
          dbFieldName: "age",
          options: {
            precision: 0
          }
        },
        {
          type: "date",
          name: "Created Date",
          dbFieldName: "created_date",
          options: {
            formatting: {
              date: "YYYY-MM-DD",
              time: "HH:mm",
              timeZone: "UTC"
            }
          }
        },
        {
          type: "longText",
          name: "Notes",
          dbFieldName: "notes",
          description: "Additional notes about the contact"
        }
      ],
      views: [
        {
          name: "Grid View",
          type: "grid",
          description: "Default grid view",
          order: 0,
          options: {
            rowHeight: "short",
            fieldNameDisplayLines: 1,
            frozenColumnCount: 0
          },
          sort: {
            sortObjs: [
              {
                fieldId: "name",
                order: "asc"
              }
            ],
            manualSort: false
          },
          filter: {
            conjunction: "and",
            filterSet: []
          },
          group: []
        }
      ],
      order: 0,
      fieldKeyType: "name"
    };
  };

  const getSampleSampleJson = () => {
    return {
      records: [
        {
          fields: {
            "Name": "John Doe",
            "Email": "john.doe@example.com",
            "Phone": "+1234567890",
            "Status": "Active",
            "Age": 35,
            "Created Date": "2024-01-15",
            "Notes": "VIP customer"
          }
        },
        {
          fields: {
            "Name": "Jane Smith",
            "Email": "jane.smith@example.com",
            "Phone": "+0987654321",
            "Status": "Active",
            "Age": 28,
            "Created Date": "2024-01-20",
            "Notes": "Regular customer"
          }
        }
      ]
    };
  };

  // Download sample JSON templates
  const downloadSampleTableJson = () => {
    const sample = getSampleTableJson();
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'teable-table-json-template.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Downloaded',
      description: 'Sample Table JSON template downloaded',
    });
  };

  const downloadSampleSampleJson = () => {
    const sample = getSampleSampleJson();
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'teable-sample-json-template.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Downloaded',
      description: 'Sample JSON template downloaded',
    });
  };

  // Load sample template into form
  const loadSampleTableJson = () => {
    const sample = getSampleTableJson();
    setTableJsonString(JSON.stringify(sample, null, 2));
    toast({
      title: 'Loaded',
      description: 'Sample Table JSON template loaded into form',
    });
  };

  const loadSampleSampleJson = () => {
    const sample = getSampleSampleJson();
    setSampleJsonString(JSON.stringify(sample, null, 2));
    toast({
      title: 'Loaded',
      description: 'Sample JSON template loaded into form',
    });
  };

  // Load configs on mount
  useEffect(() => {
    loadConfigs();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [configs, searchQuery, filterStatus, filterType]);

  // Real-time JSON validation
  useEffect(() => {
    if (tableJsonString || sampleJsonString) {
      const validation = validateJsonFields(tableJsonString, sampleJsonString);
      setJsonValidationResult(validation);
    } else {
      setJsonValidationResult(null);
    }
  }, [tableJsonString, sampleJsonString]);

  const loadConfigs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      setConfigs(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load base templates',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Group configs by base name (name field)
  type BaseGroup = {
    baseName: string;
    configs: TeableSeedConfig[];
    hasActive: boolean;
    hasInactive: boolean;
  };

  const groupConfigsByBase = (configList: TeableSeedConfig[]): BaseGroup[] => {
    const baseMap = new Map<string, TeableSeedConfig[]>();
    
    configList.forEach((config) => {
      // name field represents the base name
      const baseName = config.name;
      if (!baseMap.has(baseName)) {
        baseMap.set(baseName, []);
      }
      baseMap.get(baseName)!.push(config);
    });

    return Array.from(baseMap.entries()).map(([baseName, configs]) => ({
      baseName,
      configs: configs.sort((a, b) => a.tablename.localeCompare(b.tablename)),
      hasActive: configs.some((c) => c.status === 'active'),
      hasInactive: configs.some((c) => c.status === 'inactive'),
    })).sort((a, b) => a.baseName.localeCompare(b.baseName));
  };

  const applyFilters = () => {
    let filtered = [...configs];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (config) =>
          config.name.toLowerCase().includes(query) ||
          config.tablename.toLowerCase().includes(query) ||
          config.description?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((config) => config.status === filterStatus);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((config) => config.system_custom === filterType);
    }

    setFilteredConfigs(filtered);
  };

  const handleBaseClick = (baseName: string) => {
    setSelectedBaseName(baseName);
  };

  const handleBackToBases = () => {
    setSelectedBaseName(null);
  };

  const getBaseGroups = (): BaseGroup[] => {
    return groupConfigsByBase(filteredConfigs);
  };

  const getTablesForBase = (baseName: string): TeableSeedConfig[] => {
    return filteredConfigs.filter((config) => config.name === baseName);
  };

  // Helper functions for managing multiple table entries
  const addTableEntry = () => {
    setTableEntries([
      ...tableEntries,
      {
        tablename: '',
        table_json: {},
        sample_json: {},
        tableJsonString: '{}',
        sampleJsonString: '{}',
      },
    ]);
  };

  const removeTableEntry = (index: number) => {
    if (tableEntries.length > 1) {
      setTableEntries(tableEntries.filter((_, i) => i !== index));
    }
  };

  const updateTableEntry = (
    index: number,
    field: keyof TableEntry,
    value: string | Record<string, unknown>
  ) => {
    const updated = [...tableEntries];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setTableEntries(updated);
  };

  const handleCreate = () => {
    setFormData({
      name: selectedBaseName || '',
      description: '',
      image: '',
      tablename: '',
      table_json: {},
      sample_json: {},
      status: 'active',
      system_custom: 'custom',
    });
    setTableEntries([
      {
        tablename: '',
        table_json: {},
        sample_json: {},
        tableJsonString: '{}',
        sampleJsonString: '{}',
      },
    ]);
    setTableJsonString('{}');
    setSampleJsonString('{}');
    setDialogError(null);
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (config: TeableSeedConfig) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      description: config.description || '',
      image: config.image || '',
      tablename: config.tablename,
      table_json: config.table_json,
      sample_json: config.sample_json,
      status: config.status,
      system_custom: config.system_custom,
    });
    setTableJsonString(JSON.stringify(config.table_json, null, 2));
    setSampleJsonString(JSON.stringify(config.sample_json, null, 2));
    setDialogError(null);
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      image: '',
      tablename: '',
      table_json: {},
      sample_json: {},
      status: 'active',
      system_custom: 'custom',
    });
    setTableJsonString('{}');
    setSampleJsonString('{}');
    setDialogError(null);
  };

  const handleEditBase = (baseName: string) => {
    const baseConfigs = getTablesForBase(baseName);
    if (baseConfigs.length === 0) return;

    // Use active config if available, otherwise use first config
    const activeConfig = baseConfigs.find((c) => c.status === 'active') || baseConfigs[0];
    
    setEditingBase(baseName);
    setBaseEditFormData({
      description: activeConfig.description || '',
      image: activeConfig.image || '',
      status: activeConfig.status,
      system_custom: activeConfig.system_custom,
    });
    setDialogError(null);
  };

  const handleCancelBaseEdit = () => {
    setEditingBase(null);
    setBaseEditFormData({
      description: '',
      image: '',
      status: 'active',
      system_custom: 'custom',
    });
    setDialogError(null);
  };

  const handleView = (config: TeableSeedConfig) => {
    setSelectedConfig(config);
    setIsViewDialogOpen(true);
  };

  const handleDelete = (config: TeableSeedConfig) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteBase = (baseName: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedBaseForDelete(baseName);
    setIsDeleteBaseDialogOpen(true);
  };

  const handleConfirmDeleteBase = async () => {
    if (!selectedBaseForDelete) return;

    setIsSaving(true);
    setDialogError(null);
    try {
      // Get all configs for this base
      const baseConfigs = getTablesForBase(selectedBaseForDelete);
      const configIds = baseConfigs.map((c) => c.id);

      // Check if any are system configs (prevent deletion)
      const systemConfigs = baseConfigs.filter((c) => c.system_custom === 'system');
      if (systemConfigs.length > 0) {
        throw new Error('Cannot delete base with system templates. Please delete custom templates only.');
      }

      // Delete all configs in this base
      const { error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .delete()
        .in('id', configIds);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Base "${selectedBaseForDelete}" and all its tables deleted successfully`,
      });

      setIsDeleteBaseDialogOpen(false);
      setSelectedBaseForDelete(null);
      
      // If we're viewing this base, go back to bases list
      if (selectedBaseName === selectedBaseForDelete) {
        setSelectedBaseName(null);
      }
      
      loadConfigs();
    } catch (error: any) {
      setDialogError(error.message || 'Failed to delete base');
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete base',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCreate = async () => {
    // Validate basic form data
    const errors: string[] = [];
    if (!formData.name) errors.push('Name is required');
    if (!formData.status) errors.push('Status is required');
    if (formData.status && !['active', 'inactive'].includes(formData.status)) {
      errors.push('Status must be one of: active, inactive');
    }
    if (formData.system_custom && !['system', 'custom'].includes(formData.system_custom)) {
      errors.push('System/Custom must be one of: system, custom');
    }

    // Validate table entries
    tableEntries.forEach((entry, index) => {
      if (!entry.tablename) {
        errors.push(`Table ${index + 1}: Table name is required`);
      }
    });

    if (errors.length > 0) {
      toast({
        title: 'Validation Error',
        description: errors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    // Validate JSON for all table entries
    const jsonValidationErrors: string[] = [];
    tableEntries.forEach((entry, index) => {
      const validation = validateJsonFields(entry.tableJsonString, entry.sampleJsonString);
      if (!validation.isValid) {
        jsonValidationErrors.push(`Table ${index + 1}: ${validation.errors.join(', ')}`);
      }
    });

    if (jsonValidationErrors.length > 0) {
      toast({
        title: 'JSON Validation Error',
        description: jsonValidationErrors.join('; '),
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    setDialogError(null);
    try {
      // Prepare records for batch insert
      const recordsToInsert = tableEntries.map((entry, entryIndex) => {
        let table_json = {};
        let sample_json = {};
        
        if (entry.tableJsonString && entry.tableJsonString.trim()) {
          try {
            table_json = JSON.parse(entry.tableJsonString);
          } catch (e) {
            throw new Error(`Table ${entry.tablename || entryIndex + 1}: Invalid JSON in Table JSON`);
          }
        }

        if (entry.sampleJsonString && entry.sampleJsonString.trim()) {
          try {
            sample_json = JSON.parse(entry.sampleJsonString);
          } catch (e) {
            throw new Error(`Table ${entry.tablename || entryIndex + 1}: Invalid JSON in Sample JSON`);
          }
        }

        return {
          name: formData.name,
          description: formData.description || '',
          image: formData.image || '',
          tablename: entry.tablename,
          table_json: table_json,
          sample_json: sample_json,
          status: formData.status || 'active',
          system_custom: formData.system_custom || 'custom',
        };
      });

      // Insert all records
      const { data, error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .insert(recordsToInsert)
        .select();

      if (error) {
        // Handle unique constraint violation for versioning
        if (error.code === '23505' && error.message?.includes('sys_seed_config_status_name_tablename_key')) {
          throw new Error(
            `An active configuration with the same name and table already exists. ` +
            `Only one active version is allowed per name+table combination. ` +
            `Please either: 1) Set this configuration to "Inactive", or 2) Deactivate the existing active version first.`
          );
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: `Created ${recordsToInsert.length} table template${recordsToInsert.length === 1 ? '' : 's'} successfully`,
      });

      setIsCreateDialogOpen(false);
      loadConfigs();
    } catch (error: any) {
      // Set dialog error instead of toast so it's visible in the dialog
      setDialogError(error.message || 'Failed to create base template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedConfig) return;

    // Enhanced JSON validation
    const jsonValidation = validateJsonFields(tableJsonString, sampleJsonString);
    if (!jsonValidation.isValid) {
      toast({
        title: 'JSON Validation Error',
        description: jsonValidation.errors.join(', '),
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    setDialogError(null);
    try {
      // Parse JSON fields
      let table_json = {};
      let sample_json = {};
      
      if (tableJsonString && tableJsonString.trim()) {
        try {
          table_json = JSON.parse(tableJsonString);
        } catch (e) {
          throw new Error('Invalid JSON in Table JSON');
        }
      }

      if (sampleJsonString && sampleJsonString.trim()) {
        try {
          sample_json = JSON.parse(sampleJsonString);
        } catch (e) {
          throw new Error('Invalid JSON in Sample JSON');
        }
      }

      const updateData: UpdateTeableSeedConfigInput = {
        ...formData,
        table_json: table_json,
        sample_json: sample_json,
      };

      const { data, error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedConfig.id)
        .select()
        .single();

      if (error) {
        // Handle unique constraint violation for versioning
        if (error.code === '23505' && error.message?.includes('sys_seed_config_status_name_tablename_key')) {
          throw new Error(
            `An active configuration with name "${formData.name}" and table "${formData.tablename}" already exists. ` +
            `Only one active version is allowed per configuration. ` +
            `Please either: 1) Set this configuration to "Inactive", or 2) Deactivate the existing active version first.`
          );
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: 'Base template updated successfully',
      });

      setEditingConfig(null);
      handleCancelEdit();
      loadConfigs();
    } catch (error: any) {
      // Set dialog error instead of toast so it's visible in the dialog
      setDialogError(error.message || 'Failed to update base template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBaseEdit = async () => {
    if (!editingBase) return;

    // Validate form data
    if (!baseEditFormData.status || !['active', 'inactive'].includes(baseEditFormData.status)) {
      toast({
        title: 'Validation Error',
        description: 'Status must be one of: active, inactive',
        variant: 'destructive',
      });
      return;
    }

    if (!baseEditFormData.system_custom || !['system', 'custom'].includes(baseEditFormData.system_custom)) {
      toast({
        title: 'Validation Error',
        description: 'System/Custom must be one of: system, custom',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    setDialogError(null);

    try {
      // Get all configs for this base
      const baseConfigs = getTablesForBase(editingBase);
      
      // Check for unique constraint violations if setting status to 'active'
      if (baseEditFormData.status === 'active') {
        // Check if there are any existing active configs with same name+tablename
        // that are not in this base (would cause conflict)
        const configIds = new Set(baseConfigs.map((c) => c.id));
        const { data: allActiveConfigs, error: checkError } = await supabase
          .schema('altzero')
          .from('sys_seed_config')
          .select('id, name, tablename, status')
          .eq('status', 'active')
          .eq('name', editingBase);

        if (checkError) throw checkError;

        // Filter out configs that are in the current base
        const existingActiveConfigs = allActiveConfigs?.filter((c) => 
          !configIds.has(c.id)
        ) || [];

        // Check for conflicts: if we're activating configs, check if there are
        // other active configs with same name+tablename combinations
        const tablenames = new Set(baseConfigs.map((c) => c.tablename));
        const conflictingConfigs = existingActiveConfigs.filter((c) => 
          tablenames.has(c.tablename)
        );

        if (conflictingConfigs.length > 0) {
          const conflictingTables = conflictingConfigs.map((c) => c.tablename).join(', ');
          throw new Error(
            `Cannot activate base: There are already active configurations for tables: ${conflictingTables}. ` +
            `Please deactivate the existing active versions first, or set this base to inactive.`
          );
        }
      }

      // Update all configs in this base
      const { error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .update({
          description: baseEditFormData.description || null,
          image: baseEditFormData.image || null,
          status: baseEditFormData.status,
          system_custom: baseEditFormData.system_custom,
          updated_at: new Date().toISOString(),
        })
        .eq('name', editingBase);

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505' && error.message?.includes('sys_seed_config_status_name_tablename_key')) {
          throw new Error(
            `Cannot update base: An active configuration already exists for one or more tables. ` +
            `Only one active version is allowed per name+table combination. ` +
            `Please deactivate existing active versions first.`
          );
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: `Base "${editingBase}" updated successfully`,
      });

      handleCancelBaseEdit();
      loadConfigs();
    } catch (error: any) {
      setDialogError(error.message || 'Failed to update base');
      toast({
        title: 'Error',
        description: error.message || 'Failed to update base',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTableJsonFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select a JSON file',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content); // Validate JSON
        setTableJsonString(content);
        toast({
          title: 'File Uploaded',
          description: 'Table JSON loaded successfully',
        });
      } catch (error) {
        toast({
          title: 'Invalid JSON',
          description: 'The uploaded file contains invalid JSON',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);

    if (tableJsonFileRef.current) {
      tableJsonFileRef.current.value = '';
    }
  };

  const handleSampleJsonFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select a JSON file',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content); // Validate JSON
        setSampleJsonString(content);
        toast({
          title: 'File Uploaded',
          description: 'Sample JSON loaded successfully',
        });
      } catch (error) {
        toast({
          title: 'Invalid JSON',
          description: 'The uploaded file contains invalid JSON',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);

    if (sampleJsonFileRef.current) {
      sampleJsonFileRef.current.value = '';
    }
  };

  const validateJsonFields = (tableJson: string, sampleJson: string) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate table_json
    if (tableJson && tableJson.trim()) {
      try {
        const parsed = JSON.parse(tableJson);
        
        // Check if it's a valid object
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          errors.push('Table JSON: Must be a valid JSON object (not an array or null)');
        } else {
          // Additional validation for table_json structure
          if (Object.keys(parsed).length === 0) {
            warnings.push('Table JSON: Object is empty');
          }
        }
      } catch (e: any) {
        const errorMessage = e.message || 'Unknown error';
        const positionMatch = errorMessage.match(/position (\d+)/);
        if (positionMatch) {
          const position = parseInt(positionMatch[1]);
          const lines = tableJson.split('\n');
          let lineNum = 1;
          let charCount = 0;
          for (let i = 0; i < lines.length; i++) {
            if (charCount + lines[i].length + 1 > position) {
              lineNum = i + 1;
              break;
            }
            charCount += lines[i].length + 1;
          }
          errors.push(`Table JSON: Invalid JSON syntax at line ${lineNum} - ${errorMessage}`);
        } else {
          errors.push(`Table JSON: Invalid JSON - ${errorMessage}`);
        }
      }
    } else if (tableJson.trim() === '') {
      warnings.push('Table JSON: Empty JSON (will be saved as empty object)');
    }

    // Validate sample_json
    if (sampleJson && sampleJson.trim()) {
      try {
        const parsed = JSON.parse(sampleJson);
        
        // Check if it's a valid object
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          errors.push('Sample JSON: Must be a valid JSON object (not an array or null)');
        } else {
          // Additional validation for sample_json structure
          if (Object.keys(parsed).length === 0) {
            warnings.push('Sample JSON: Object is empty');
          }
        }
      } catch (e: any) {
        const errorMessage = e.message || 'Unknown error';
        const positionMatch = errorMessage.match(/position (\d+)/);
        if (positionMatch) {
          const position = parseInt(positionMatch[1]);
          const lines = sampleJson.split('\n');
          let lineNum = 1;
          let charCount = 0;
          for (let i = 0; i < lines.length; i++) {
            if (charCount + lines[i].length + 1 > position) {
              lineNum = i + 1;
              break;
            }
            charCount += lines[i].length + 1;
          }
          errors.push(`Sample JSON: Invalid JSON syntax at line ${lineNum} - ${errorMessage}`);
        } else {
          errors.push(`Sample JSON: Invalid JSON - ${errorMessage}`);
        }
      }
    } else if (sampleJson.trim() === '') {
      warnings.push('Sample JSON: Empty JSON (will be saved as empty object)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  };

  // Bulk Import Functions
  const validateBulkImportData = (data: BulkImportData, selectedBaseName: string | null): BulkImportPreview | null => {
    const errors: string[] = [];
    
    // Validate JSON structure
    if (!data.tables || !Array.isArray(data.tables)) {
      errors.push('Invalid format: "tables" must be an array');
      return null;
    }

    if (data.tables.length === 0) {
      errors.push('No tables found in import file');
      return null;
    }

    // Validate base name if provided
    if (data.baseName && selectedBaseName && data.baseName !== selectedBaseName) {
      errors.push(`Base name mismatch: File specifies "${data.baseName}" but you're adding to "${selectedBaseName}"`);
    }

    // Check for duplicate tablenames
    const tablenames = data.tables.map(t => t.tablename);
    const duplicates = tablenames.filter((name, index) => tablenames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      errors.push(`Duplicate tablenames found: ${[...new Set(duplicates)].join(', ')}`);
    }

    // Validate each table
    const validatedTables = data.tables.map((table, index) => {
      const tableErrors: string[] = [];
      const tableWarnings: string[] = [];

      // Check required fields
      if (!table.tablename || typeof table.tablename !== 'string' || table.tablename.trim() === '') {
        tableErrors.push('Table name is required');
      }

      if (!table.table_json || typeof table.table_json !== 'object' || Array.isArray(table.table_json)) {
        tableErrors.push('table_json must be a valid object');
      }

      if (!table.sample_json || typeof table.sample_json !== 'object' || Array.isArray(table.sample_json)) {
        tableErrors.push('sample_json must be a valid object');
      }

      // Validate JSON structure using existing validation
      if (table.table_json && table.sample_json) {
        const tableJsonString = JSON.stringify(table.table_json);
        const sampleJsonString = JSON.stringify(table.sample_json);
        const validation = validateJsonFields(tableJsonString, sampleJsonString);
        
        if (!validation.isValid) {
          tableErrors.push(...validation.errors);
        }
        tableWarnings.push(...validation.warnings);
      }

      return {
        tablename: table.tablename || `table_${index + 1}`,
        table_json: table.table_json || {},
        sample_json: table.sample_json || {},
        validation: {
          isValid: tableErrors.length === 0,
          errors: tableErrors,
          warnings: tableWarnings,
        },
      };
    });

    const validTables = validatedTables.filter(t => t.validation.isValid).length;
    const invalidTables = validatedTables.filter(t => !t.validation.isValid).length;

    return {
      baseName: data.baseName,
      description: data.description,
      image: data.image,
      status: data.status || 'active',
      system_custom: data.system_custom || 'custom',
      tables: validatedTables,
      totalTables: validatedTables.length,
      validTables,
      invalidTables,
    };
  };

  const processBulkImportFile = (file: File) => {
    if (!file.name.endsWith('.json')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select a JSON file',
        variant: 'destructive',
      });
      return;
    }

    // Check file size (warn if > 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Large File',
        description: 'File is larger than 10MB. Processing may take longer.',
        variant: 'default',
      });
    }

    setBulkImportFile(file);
    setIsValidatingBulkImport(true);
    setBulkImportErrors([]);
    setBulkImportPreview(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data: BulkImportData = JSON.parse(content);
        
        // Validate the data
        const preview = validateBulkImportData(data, selectedBaseName);
        
        if (!preview) {
          setBulkImportErrors(['Failed to validate import file. Please check the format.']);
          setIsValidatingBulkImport(false);
          return;
        }

        // Check for validation errors
        if (preview.invalidTables > 0) {
          const allErrors = preview.tables
            .filter(t => !t.validation.isValid)
            .flatMap(t => t.validation.errors.map(err => `${t.tablename}: ${err}`));
          setBulkImportErrors(allErrors);
        }

        setBulkImportPreview(preview);
        setIsValidatingBulkImport(false);

        toast({
          title: 'File Loaded',
          description: `Found ${preview.totalTables} tables. ${preview.validTables} valid, ${preview.invalidTables} invalid.`,
        });
      } catch (error: any) {
        setBulkImportErrors([error.message || 'Invalid JSON file']);
        setIsValidatingBulkImport(false);
        toast({
          title: 'Invalid JSON',
          description: 'The uploaded file contains invalid JSON',
          variant: 'destructive',
        });
      }
    };
    reader.readAsText(file);
  };

  const handleBulkImportFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    processBulkImportFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processBulkImportFile(file);
    }
  };

  const handleBulkImportSave = async () => {
    if (!bulkImportPreview) return;

    setIsSaving(true);
    setBulkImportErrors([]);

    try {
      // Get base name
      // Priority: selectedBaseName (if adding to existing) > bulkImportPreview.baseName > formData.name
      const baseName = selectedBaseName || bulkImportPreview.baseName || formData.name;
      if (!baseName) {
        throw new Error('Base name is required');
      }

      // Get base-level fields
      // If adding to existing base, use existing base values; otherwise use preview or form defaults
      const baseDescription = bulkImportPreview.description || formData.description || '';
      const baseImage = bulkImportPreview.image || formData.image || '';
      const baseStatus = bulkImportPreview.status || formData.status || 'active';
      const baseSystemCustom = bulkImportPreview.system_custom || formData.system_custom || 'custom';

      // Prepare records for batch insert (only valid tables)
      const validTables = bulkImportPreview.tables.filter(t => t.validation.isValid);
      
      if (validTables.length === 0) {
        throw new Error('No valid tables to import');
      }

      const recordsToInsert = validTables.map((table) => ({
        name: baseName,
        description: baseDescription,
        image: baseImage,
        tablename: table.tablename,
        table_json: table.table_json,
        sample_json: table.sample_json,
        status: baseStatus,
        system_custom: baseSystemCustom,
      }));

      // Batch insert
      const { data, error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .insert(recordsToInsert)
        .select();

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505' && error.message?.includes('sys_seed_config_status_name_tablename_key')) {
          throw new Error(
            `Cannot import: An active configuration already exists for one or more tables. ` +
            `Only one active version is allowed per name+table combination. ` +
            `Please deactivate existing active versions first or set status to inactive.`
          );
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: `Successfully imported ${validTables.length} table${validTables.length === 1 ? '' : 's'}`,
      });

      // Close dialog and reset
      setIsBulkImportDialogOpen(false);
      setBulkImportPreview(null);
      setBulkImportFile(null);
      setBulkImportErrors([]);
      
      // Reload configs
      loadConfigs();

      // If creating new base, close create dialog and show the new base
      if (!selectedBaseName && baseName) {
        setIsCreateDialogOpen(false);
        setSelectedBaseName(baseName);
      }
    } catch (error: any) {
      setBulkImportErrors([error.message || 'Failed to import tables']);
      toast({
        title: 'Error',
        description: error.message || 'Failed to import tables',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const downloadBulkImportTemplate = () => {
    const template: BulkImportData = {
      baseName: 'example_base',
      description: 'Example base description',
      image: 'https://example.com/icon.png',
      status: 'active',
      system_custom: 'custom',
      tables: [
        {
          tablename: 'contacts',
          table_json: getSampleTableJson(),
          sample_json: getSampleSampleJson(),
        },
        {
          tablename: 'opportunities',
          table_json: getSampleTableJson(),
          sample_json: getSampleSampleJson(),
        },
      ],
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk-import-template.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Downloaded',
      description: 'Bulk import template downloaded',
    });
  };

  const handleBulkImportOpen = () => {
    setIsBulkImportDialogOpen(true);
    setBulkImportPreview(null);
    setBulkImportFile(null);
    setBulkImportErrors([]);
    if (bulkImportFileRef.current) {
      bulkImportFileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold">
            {editingBase ? `Edit Base: ${editingBase}` : editingConfig ? `Edit Template: ${editingConfig.tablename}` : selectedBaseName ? `Tables in ${selectedBaseName}` : 'Base Templates'}
          </h2>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadConfigs} variant="ghost" size="sm" disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          {!selectedBaseName && !editingConfig && !editingBase && (
            <Button onClick={handleCreate} variant="default" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          )}
        </div>
      </div>

      {/* Edit Form - Inline Page View */}
      {editingConfig ? (
        <div className="space-y-6">
          {/* Back Button */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
              className="gap-2"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back
            </Button>
          </div>

          {/* Edit Form */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_name">Name *</Label>
                    <Input
                      id="edit_name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_tablename">Table Name *</Label>
                    <Input
                      id="edit_tablename"
                      value={formData.tablename}
                      onChange={(e) =>
                        setFormData({ ...formData, tablename: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_description">Description</Label>
                  <Textarea
                    id="edit_description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_image">Image URL</Label>
                  <Input
                    id="edit_image"
                    value={formData.image}
                    onChange={(e) =>
                      setFormData({ ...formData, image: e.target.value })
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_status">Status *</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={formData.status === 'active' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, status: 'active' })}
                        className="flex-1"
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        variant={formData.status === 'inactive' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, status: 'inactive' })}
                        className="flex-1"
                      >
                        Inactive
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Only one active version allowed per name+table combination. Multiple inactive versions can exist.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_system_custom">Type *</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={formData.system_custom === 'system' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, system_custom: 'system' })}
                        className="flex-1"
                      >
                        System
                      </Button>
                      <Button
                        type="button"
                        variant={formData.system_custom === 'custom' ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, system_custom: 'custom' })}
                        className="flex-1"
                      >
                        Custom
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit_table_json">Table JSON</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={showVisualEditor ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowVisualEditor(!showVisualEditor)}
                        className="text-xs"
                      >
                        {showVisualEditor ? "Visual Editor" : "JSON Editor"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={downloadSampleTableJson}
                        className="text-xs"
                        title="Download sample template"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Template
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={loadSampleTableJson}
                        className="text-xs"
                        title="Load sample template into form"
                      >
                        Load Sample
                      </Button>
                    </div>
                  </div>
                  {showVisualEditor ? (
                    <div className="border rounded-lg p-4 bg-card">
                      <TableFieldsEditor
                        value={tableJsonString}
                        onChange={setTableJsonString}
                      />
                    </div>
                  ) : (
                    <Textarea
                      id="edit_table_json"
                      value={tableJsonString}
                      onChange={(e) => setTableJsonString(e.target.value)}
                      rows={8}
                      className={`font-mono text-sm ${
                        jsonValidationResult && !jsonValidationResult.isValid
                          ? 'border-red-300 focus:border-red-500'
                          : jsonValidationResult?.isValid
                          ? 'border-green-300 focus:border-green-500'
                          : ''
                      }`}
                    />
                  )}
                  {jsonValidationResult && (
                    <div className="text-xs space-y-1">
                      {jsonValidationResult.errors.filter(e => e.includes('Table JSON')).map((error, index) => (
                        <div key={index} className="text-red-600 flex items-center">
                          <XCircle className="w-3 h-3 mr-1" />
                          {error}
                        </div>
                      ))}
                      {jsonValidationResult.warnings.filter(w => w.includes('Table JSON')).map((warning, index) => (
                        <div key={index} className="text-yellow-600 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="edit_sample_json">Sample JSON</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={downloadSampleSampleJson}
                        className="text-xs"
                        title="Download sample template"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Template
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={loadSampleSampleJson}
                        className="text-xs"
                        title="Load sample template into form"
                      >
                        Load Sample
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    id="edit_sample_json"
                    value={sampleJsonString}
                    onChange={(e) => setSampleJsonString(e.target.value)}
                    rows={8}
                    className={`font-mono text-sm ${
                      jsonValidationResult && !jsonValidationResult.isValid
                        ? 'border-red-300 focus:border-red-500'
                        : jsonValidationResult?.isValid
                        ? 'border-green-300 focus:border-green-500'
                        : ''
                    }`}
                  />
                  {jsonValidationResult && (
                    <div className="text-xs space-y-1">
                      {jsonValidationResult.errors.filter(e => e.includes('Sample JSON')).map((error, index) => (
                        <div key={index} className="text-red-600 flex items-center">
                          <XCircle className="w-3 h-3 mr-1" />
                          {error}
                        </div>
                      ))}
                      {jsonValidationResult.warnings.filter(w => w.includes('Sample JSON')).map((warning, index) => (
                        <div key={index} className="text-yellow-600 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {dialogError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{dialogError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Update
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : editingBase ? (
        <div className="space-y-6">
          {/* Back Button */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelBaseEdit}
              className="gap-2"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back
            </Button>
          </div>

          {/* Base Edit Form */}
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="base_edit_name">Base Name</Label>
                  <Input
                    id="base_edit_name"
                    value={editingBase}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Base name cannot be changed as it is used as the grouping key for all tables in this base.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="base_edit_description">Description</Label>
                  <Textarea
                    id="base_edit_description"
                    value={baseEditFormData.description}
                    onChange={(e) =>
                      setBaseEditFormData({ ...baseEditFormData, description: e.target.value })
                    }
                    rows={3}
                    placeholder="Describe this base template..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="base_edit_image">Image URL</Label>
                  <Input
                    id="base_edit_image"
                    value={baseEditFormData.image}
                    onChange={(e) =>
                      setBaseEditFormData({ ...baseEditFormData, image: e.target.value })
                    }
                    placeholder="https://example.com/image.png"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="base_edit_status">Status *</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={baseEditFormData.status === 'active' ? 'default' : 'outline'}
                        onClick={() => setBaseEditFormData({ ...baseEditFormData, status: 'active' })}
                        className="flex-1"
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        variant={baseEditFormData.status === 'inactive' ? 'default' : 'outline'}
                        onClick={() => setBaseEditFormData({ ...baseEditFormData, status: 'inactive' })}
                        className="flex-1"
                      >
                        Inactive
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This will update the status for all tables in this base. Only one active version allowed per name+table combination.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="base_edit_system_custom">Type *</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={baseEditFormData.system_custom === 'system' ? 'default' : 'outline'}
                        onClick={() => setBaseEditFormData({ ...baseEditFormData, system_custom: 'system' })}
                        className="flex-1"
                      >
                        System
                      </Button>
                      <Button
                        type="button"
                        variant={baseEditFormData.system_custom === 'custom' ? 'default' : 'outline'}
                        onClick={() => setBaseEditFormData({ ...baseEditFormData, system_custom: 'custom' })}
                        className="flex-1"
                      >
                        Custom
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This will update the type for all tables in this base.
                    </p>
                  </div>
                </div>

                {dialogError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{dialogError}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={handleCancelBaseEdit}
                    disabled={isSaving}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={handleSaveBaseEdit} disabled={isSaving}>
                    {isSaving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Update Base
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <>
          {/* Filters - Compact */}
          {!selectedBaseName && (
        <div className="flex items-center gap-3 mb-4">
          <TextField
            placeholder="Search bases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="w-4 h-4 text-muted-foreground" />
                </InputAdornment>
              ),
            }}
            size="small"
            className="w-64"
          />

          <FormControl size="small" className="w-32">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" className="w-32">
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value as FilterType)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="system">System</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}

      {/* Base Detail View (when base is selected) */}
      {selectedBaseName ? (
        <div className="space-y-6">
          {/* Back Button */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToBases}
              className="gap-2"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Bases
            </Button>
          </div>

          {/* Base Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold mb-1">{selectedBaseName}</h2>
              <p className="text-muted-foreground">
                {getTablesForBase(selectedBaseName).length} {getTablesForBase(selectedBaseName).length === 1 ? 'table template' : 'table templates'}
              </p>
            </div>
            {!editingConfig && !editingBase && (
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleEditBase(selectedBaseName)} 
                  variant="outline" 
                  size="sm"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Base
                </Button>
                <Button 
                  onClick={handleBulkImportOpen} 
                  variant="outline" 
                  size="sm"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Import
                </Button>
                <Button onClick={handleCreate} variant="default" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Table
                </Button>
              </div>
            )}
          </div>

          {/* Tables Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Loading tables...</p>
              </div>
            </div>
          ) : getTablesForBase(selectedBaseName).length === 0 ? (
            <Card>
              <CardContent className="py-20">
                <div className="text-center">
                  <Database className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No tables found</h3>
                  <p className="text-muted-foreground mb-6">
                    No table templates found for this base
                  </p>
                  <Button onClick={handleCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Table Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getTablesForBase(selectedBaseName).map((config) => (
                <Card 
                  key={config.id} 
                  className="group hover:shadow-md transition-all duration-200 cursor-pointer border hover:border-primary/30"
                  onClick={() => handleEdit(config)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {/* Icon */}
                      {config.image ? (
                        <div className="w-10 h-10 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                          <img 
                            src={config.image} 
                            alt={config.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                          <Database className="w-6 h-6 text-foreground" strokeWidth={1.5} />
                        </div>
                      )}
                      
                      {/* Table Name */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-base truncate group-hover:text-primary transition-colors">
                          {config.tablename}
                        </h3>
                      </div>

                      {/* Status Indicator */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {config.status === 'active' ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="text-xs text-muted-foreground hidden sm:inline">Active</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                            <span className="text-xs text-muted-foreground hidden sm:inline">Inactive</span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(config);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(config);
                          }}
                          disabled={config.system_custom === 'system'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Bases Grid Layout */
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Loading templates...</p>
              </div>
            </div>
          ) : filteredConfigs.length === 0 ? (
            <Card>
              <CardContent className="py-20">
                <div className="text-center">
                  <Database className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No templates found</h3>
                  <p className="text-muted-foreground mb-6">
                    {searchQuery || filterStatus !== 'all' || filterType !== 'all' 
                      ? 'Try adjusting your filters' 
                      : 'Get started by creating your first base template'}
                  </p>
                  {(!searchQuery && filterStatus === 'all' && filterType === 'all') && (
                    <Button onClick={handleCreate}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {getBaseGroups().map((baseGroup) => {
                const firstConfig = baseGroup.configs[0];
                const activeConfig = baseGroup.configs.find((c) => c.status === 'active') || firstConfig;
                
                return (
                  <Card 
                    key={baseGroup.baseName}
                    className="group hover:shadow-md transition-all duration-200 cursor-pointer border hover:border-primary/30"
                    onClick={() => handleBaseClick(baseGroup.baseName)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {/* Icon */}
                        {activeConfig?.image ? (
                          <div className="w-10 h-10 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img 
                              src={activeConfig.image} 
                              alt={baseGroup.baseName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                            <Database className="w-6 h-6 text-foreground" strokeWidth={1.5} />
                          </div>
                        )}
                        
                        {/* Base Name */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-base truncate group-hover:text-primary transition-colors">
                            {baseGroup.baseName}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {baseGroup.configs.length} {baseGroup.configs.length === 1 ? 'table' : 'tables'}
                          </p>
                        </div>

                        {/* Status Indicator */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {baseGroup.hasActive ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span className="text-xs text-muted-foreground hidden sm:inline">Active</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                              <span className="text-xs text-muted-foreground hidden sm:inline">Inactive</span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBase(baseGroup.baseName);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBase(baseGroup.baseName, e);
                            }}
                            disabled={baseGroup.configs.some((c) => c.system_custom === 'system')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) setDialogError(null);
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create Base Template {tableEntries.length > 1 ? `(${tableEntries.length} tables)` : ''}
            </DialogTitle>
            <DialogDescription>
              Create a new base template configuration with multiple tables
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Base-level fields */}
            <div className="space-y-4 border-b pb-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Base Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., crm, scopingAi"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image">Image URL</Label>
                  <Input
                    id="image"
                    value={formData.image}
                    onChange={(e) =>
                      setFormData({ ...formData, image: e.target.value })
                    }
                    placeholder="https://example.com/image.png"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe this base template..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.status === 'active' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, status: 'active' })}
                      className="flex-1"
                    >
                      Active
                    </Button>
                    <Button
                      type="button"
                      variant={formData.status === 'inactive' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, status: 'inactive' })}
                      className="flex-1"
                    >
                      Inactive
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Only one active version allowed per name+table combination. Multiple inactive versions can exist.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="system_custom">Type *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={formData.system_custom === 'system' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, system_custom: 'system' })}
                      className="flex-1"
                    >
                      System
                    </Button>
                    <Button
                      type="button"
                      variant={formData.system_custom === 'custom' ? 'default' : 'outline'}
                      onClick={() => setFormData({ ...formData, system_custom: 'custom' })}
                      className="flex-1"
                    >
                      Custom
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tables section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Tables *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleBulkImportOpen}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Import
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTableEntry}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Table
                  </Button>
                </div>
              </div>

              {tableEntries.map((entry, index) => {
                const entryValidation = validateJsonFields(entry.tableJsonString, entry.sampleJsonString);
                return (
                  <Card key={index} className="p-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Table {index + 1}</h4>
                        {tableEntries.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTableEntry(index)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`tablename-${index}`}>Table Name *</Label>
                        <Input
                          id={`tablename-${index}`}
                          value={entry.tablename}
                          onChange={(e) =>
                            updateTableEntry(index, 'tablename', e.target.value)
                          }
                          placeholder="e.g., contacts, opportunities"
                        />
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={`table_json-${index}`}>Table JSON</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={showVisualEditor ? "default" : "outline"}
                              size="sm"
                              onClick={() => setShowVisualEditor(!showVisualEditor)}
                              className="text-xs"
                            >
                              {showVisualEditor ? "Visual Editor" : "JSON Editor"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const sample = getSampleTableJson();
                                updateTableEntry(index, 'tableJsonString', JSON.stringify(sample, null, 2));
                              }}
                              className="text-xs"
                            >
                              Load Sample
                            </Button>
                          </div>
                        </div>
                        {showVisualEditor ? (
                          <div className="border rounded-lg p-4 bg-card">
                            <TableFieldsEditor
                              value={entry.tableJsonString}
                              onChange={(value) => updateTableEntry(index, 'tableJsonString', value)}
                            />
                          </div>
                        ) : (
                          <Textarea
                            id={`table_json-${index}`}
                            value={entry.tableJsonString}
                            onChange={(e) =>
                              updateTableEntry(index, 'tableJsonString', e.target.value)
                            }
                            placeholder='{"fields": [], "relationships": []}'
                            rows={6}
                            className={`font-mono text-sm ${
                              entryValidation && !entryValidation.isValid
                                ? 'border-red-300 focus:border-red-500'
                                : entryValidation?.isValid
                                ? 'border-green-300 focus:border-green-500'
                                : ''
                            }`}
                          />
                        )}
                        {entryValidation && !entryValidation.isValid && (
                          <div className="text-xs space-y-1">
                            {entryValidation.errors.filter(e => e.includes('Table JSON')).map((error, errIndex) => (
                              <div key={errIndex} className="text-red-600 flex items-center">
                                <XCircle className="w-3 h-3 mr-1" />
                                Table {index + 1}: {error}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={`sample_json-${index}`}>Sample JSON</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const sample = getSampleSampleJson();
                              updateTableEntry(index, 'sampleJsonString', JSON.stringify(sample, null, 2));
                            }}
                            className="text-xs"
                          >
                            Load Sample
                          </Button>
                        </div>
                        <Textarea
                          id={`sample_json-${index}`}
                          value={entry.sampleJsonString}
                          onChange={(e) =>
                            updateTableEntry(index, 'sampleJsonString', e.target.value)
                          }
                          placeholder='{"samples": []}'
                          rows={6}
                          className={`font-mono text-sm ${
                            entryValidation && !entryValidation.isValid
                              ? 'border-red-300 focus:border-red-500'
                              : entryValidation?.isValid
                              ? 'border-green-300 focus:border-green-500'
                              : ''
                          }`}
                        />
                        {entryValidation && !entryValidation.isValid && (
                          <div className="text-xs space-y-1">
                            {entryValidation.errors.filter(e => e.includes('Sample JSON')).map((error, errIndex) => (
                              <div key={errIndex} className="text-red-600 flex items-center">
                                <XCircle className="w-3 h-3 mr-1" />
                                Table {index + 1}: {error}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {dialogError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{dialogError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveCreate} disabled={isSaving}>
              {isSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Create {tableEntries.length} Table{tableEntries.length === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Base Template Details</DialogTitle>
            <DialogDescription>
              {selectedConfig?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedConfig && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedConfig.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Table Name</Label>
                  <p className="font-medium">{selectedConfig.tablename}</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="font-medium">{selectedConfig.description || 'N/A'}</p>
              </div>

              {selectedConfig.image && (
                <div>
                  <Label className="text-muted-foreground">Image</Label>
                  <img 
                    src={selectedConfig.image} 
                    alt={selectedConfig.name}
                    className="max-w-xs mt-2 rounded"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p>
                    {selectedConfig.status === 'active' ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" />
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p>
                    <Chip 
                      label={selectedConfig.system_custom} 
                      variant="outlined" 
                      size="small" 
                    />
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Table JSON</Label>
                <pre className="mt-2 p-4 bg-muted rounded-md overflow-x-auto text-xs">
                  {JSON.stringify(selectedConfig.table_json, null, 2)}
                </pre>
              </div>

              <div>
                <Label className="text-muted-foreground">Sample JSON</Label>
                <pre className="mt-2 p-4 bg-muted rounded-md overflow-x-auto text-xs">
                  {JSON.stringify(selectedConfig.sample_json, null, 2)}
                </pre>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p>{new Date(selectedConfig.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Updated</Label>
                  <p>{new Date(selectedConfig.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Table Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Table Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this table template?
            </DialogDescription>
          </DialogHeader>

          {selectedConfig && (
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Warning</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You are about to delete the table template:{' '}
                      <strong>{selectedConfig.tablename}</strong> from base{' '}
                      <strong>{selectedConfig.name}</strong>
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      This action cannot be undone. The configuration will be permanently
                      removed from the database.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p>
                  <strong>Base:</strong> {selectedConfig.name}
                </p>
                <p>
                  <strong>Table Name:</strong> {selectedConfig.tablename}
                </p>
                <p>
                  <strong>Type:</strong> {selectedConfig.system_custom}
                </p>
                {selectedConfig.system_custom === 'system' && (
                  <p className="text-destructive text-xs mt-2">
                    System templates cannot be deleted.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSelectedConfig(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!selectedConfig) return;
                
                if (selectedConfig.system_custom === 'system') {
                  toast({
                    title: 'Error',
                    description: 'Cannot delete system templates',
                    variant: 'destructive',
                  });
                  setIsDeleteDialogOpen(false);
                  setSelectedConfig(null);
                  return;
                }

                setIsSaving(true);
                try {
                  const { error } = await supabase
                    .schema('altzero')
                    .from('sys_seed_config')
                    .delete()
                    .eq('id', selectedConfig.id);

                  if (error) throw error;

                  toast({
                    title: 'Success',
                    description: 'Table template deleted successfully',
                  });

                  setIsDeleteDialogOpen(false);
                  setSelectedConfig(null);
                  loadConfigs();
                } catch (error: any) {
                  toast({
                    title: 'Error',
                    description: error.message || 'Failed to delete table template',
                    variant: 'destructive',
                  });
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving || selectedConfig?.system_custom === 'system'}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Table
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={isBulkImportDialogOpen} onOpenChange={(open) => {
        setIsBulkImportDialogOpen(open);
        if (!open) {
          setBulkImportPreview(null);
          setBulkImportFile(null);
          setBulkImportErrors([]);
          if (bulkImportFileRef.current) {
            bulkImportFileRef.current.value = '';
          }
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Tables</DialogTitle>
            <DialogDescription>
              Upload a JSON file containing multiple table definitions. {selectedBaseName && `Tables will be added to "${selectedBaseName}".`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* File Upload Section */}
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDraggingOver
                    ? 'border-primary bg-primary/5'
                    : 'hover:border-primary/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${isDraggingOver ? 'text-primary' : 'text-muted-foreground'}`} />
                <input
                  ref={bulkImportFileRef}
                  type="file"
                  accept=".json"
                  onChange={handleBulkImportFileUpload}
                  className="hidden"
                  id="bulk-import-file"
                />
                <label htmlFor="bulk-import-file" className="cursor-pointer">
                  <Button variant="outline" type="button" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose JSON File
                    </span>
                  </Button>
                </label>
                {bulkImportFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Selected: {bulkImportFile.name} ({(bulkImportFile.size / 1024).toFixed(2)} KB)
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-4">
                  Drag and drop a JSON file or click to browse
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={downloadBulkImportTemplate}
                  className="mt-4"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {isValidatingBulkImport && (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground mr-2" />
                  <p className="text-sm text-muted-foreground">Validating import file...</p>
                </div>
              )}

              {bulkImportErrors.length > 0 && !bulkImportPreview && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Import Errors</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {bulkImportErrors.map((error, index) => (
                        <li key={index} className="text-sm">{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Preview Section */}
            {bulkImportPreview && (
              <div className="space-y-4">
                {/* Base Info Preview */}
                {(bulkImportPreview.baseName || bulkImportPreview.description || bulkImportPreview.image) && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-3">Base Information</h3>
                      <div className="space-y-2 text-sm">
                        {bulkImportPreview.baseName && (
                          <div>
                            <span className="font-medium">Base Name:</span> {bulkImportPreview.baseName}
                          </div>
                        )}
                        {bulkImportPreview.description && (
                          <div>
                            <span className="font-medium">Description:</span> {bulkImportPreview.description}
                          </div>
                        )}
                        {bulkImportPreview.image && (
                          <div>
                            <span className="font-medium">Image:</span> {bulkImportPreview.image}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Status:</span> {bulkImportPreview.status}
                        </div>
                        <div>
                          <span className="font-medium">Type:</span> {bulkImportPreview.system_custom}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Validation Summary */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Import Summary</h3>
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span>{bulkImportPreview.validTables} Valid</span>
                        </div>
                        {bulkImportPreview.invalidTables > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <span>{bulkImportPreview.invalidTables} Invalid</span>
                          </div>
                        )}
                        <div className="text-muted-foreground">
                          Total: {bulkImportPreview.totalTables}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tables List Preview */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3">Tables Preview</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {bulkImportPreview.tables.map((table, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-2 rounded border ${
                            table.validation.isValid
                              ? 'border-green-200 bg-green-50'
                              : 'border-red-200 bg-red-50'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {table.validation.isValid ? (
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              )}
                              <span className="font-medium">{table.tablename}</span>
                            </div>
                            {!table.validation.isValid && table.validation.errors.length > 0 && (
                              <div className="text-xs text-red-600 mt-1 ml-4">
                                {table.validation.errors[0]}
                                {table.validation.errors.length > 1 && ` (+${table.validation.errors.length - 1} more)`}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Validation Errors */}
                {bulkImportPreview.invalidTables > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Validation Errors</AlertTitle>
                    <AlertDescription>
                      <p className="mb-2">Some tables have validation errors. Please fix them before importing.</p>
                      <ul className="list-disc list-inside space-y-1 text-sm max-h-40 overflow-y-auto">
                        {bulkImportPreview.tables
                          .filter(t => !t.validation.isValid)
                          .flatMap(t => t.validation.errors.map(err => `${t.tablename}: ${err}`))
                          .map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkImportDialogOpen(false);
                setBulkImportPreview(null);
                setBulkImportFile(null);
                setBulkImportErrors([]);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkImportSave}
              disabled={!bulkImportPreview || bulkImportPreview.invalidTables > 0 || bulkImportPreview.validTables === 0 || isSaving}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import {bulkImportPreview?.validTables || 0} Table{bulkImportPreview?.validTables !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Base Confirmation Dialog */}
      <Dialog open={isDeleteBaseDialogOpen} onOpenChange={(open) => {
        setIsDeleteBaseDialogOpen(open);
        if (!open) {
          setSelectedBaseForDelete(null);
          setDialogError(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Base</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this base and all its tables?
            </DialogDescription>
          </DialogHeader>

          {selectedBaseForDelete && (
            <div className="space-y-4">
              {dialogError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              )}

              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">Warning</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      You are about to delete the base:{' '}
                      <strong>{selectedBaseForDelete}</strong>
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      This will delete <strong>{getTablesForBase(selectedBaseForDelete).length}</strong>{' '}
                      {getTablesForBase(selectedBaseForDelete).length === 1 ? 'table' : 'tables'} in this base.
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      This action cannot be undone. All configurations will be permanently
                      removed from the database.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p>
                  <strong>Base Name:</strong> {selectedBaseForDelete}
                </p>
                <p>
                  <strong>Tables:</strong> {getTablesForBase(selectedBaseForDelete).length}
                </p>
                {getTablesForBase(selectedBaseForDelete).some((c) => c.system_custom === 'system') && (
                  <p className="text-destructive text-xs mt-2">
                    This base contains system templates and cannot be deleted.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteBaseDialogOpen(false);
                setSelectedBaseForDelete(null);
                setDialogError(null);
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteBase}
              disabled={
                isSaving ||
                (selectedBaseForDelete &&
                  getTablesForBase(selectedBaseForDelete).some((c) => c.system_custom === 'system'))
              }
            >
              {isSaving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Base
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeableConfigPage;

